'use strict';
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var snakeCase = require('../../utils/string').snakeCase;
var LENGTH_VARIANTS = require('../../data_types').LENGTH_VARIANTS;
/**
 * Find the corresponding JavaScript type of the type in database.
 * @param {string} dataType
 */
function findJsType(DataTypes, type, dataType) {
    if (type instanceof DataTypes.VIRTUAL)
        return '';
    if (type instanceof DataTypes.BOOLEAN)
        return Boolean;
    if (type instanceof DataTypes.JSON)
        return JSON;
    if (type instanceof DataTypes.BINARY || type instanceof DataTypes.BLOB) {
        return Buffer;
    }
    switch (dataType.toLowerCase().split('(')[0]) {
        case 'boolean':
            return Boolean;
        case 'bigint':
        case 'mediumint':
        case 'smallint':
        case 'tinyint':
        case 'int':
        case 'integer':
        case 'decimal':
            return Number;
        case 'date':
        case 'datetime':
        case 'timestamp':
            return Date;
        case 'jsonb':
        case 'json':
            return JSON;
        case 'longtext':
        case 'mediumtext':
        case 'text':
        case 'varchar':
        case 'char':
        default:
            return String;
    }
}
/**
 * Find and instantiate the database specific data type
 * @param {Function} DataTypes
 * @param {Object} params
 * @param {Function} params.type
 * @param {string} params.dataType
 */
function createType(DataTypes, params) {
    var dataType = params.dataType, type = params.type;
    if (!type)
        return DataTypes.findType(dataType);
    if (type && typeof type === 'function') {
        var DataType_1 = DataTypes[type.name] || type;
        return new DataType_1();
    }
    var DataType = DataTypes[type.constructor.name];
    if (!DataType)
        return type;
    switch (type.constructor.name) {
        case 'DATE':
            return new DataType(type.precision, type.timezone);
        case 'DECIMAL':
            return new DataType(type.precision, type.scale);
        case 'TINYINT':
        case 'SMALLINT':
        case 'MEDIUMINT':
        case 'INTEGER':
        case 'BIGINT':
            return new DataType(type.dataLength, type.unsigned, type.zerofill);
        case 'BINARY':
        case 'VARBINARY':
        case 'CHAR':
        case 'VARCHAR':
        case 'STRING':
        case 'TEXT':
            return new DataType(type.dataLength);
        default:
            return new DataType();
    }
}
var Attribute = /** @class */ (function () {
    /**
     * Attribute name and definition
     * @param {string} name attribute name
     * @param {Object} params attribute definition
     * @param {Object} opts
     * @param {boolean} opts.underscored
     */
    function Attribute(name, params, _a) {
        var _b = _a === void 0 ? {} : _a, underscored = _b.underscored;
        var DataTypes = this.constructor.DataTypes;
        if (params instanceof Attribute)
            return params;
        var columnName = underscored === false ? name : snakeCase(name);
        if (params == null)
            return Object.assign(this, { name: name, columnName: columnName });
        // { foo: STRING }
        // { foo: STRING(255) }
        if (typeof params === 'function' || DataTypes.is(params)) {
            params = { type: params };
        }
        var type = createType(DataTypes, params);
        var dataType = params.dataType || type.dataType;
        var _c = params.defaultValue, defaultValue = _c === void 0 ? null : _c;
        try {
            // normalize column defaults like `'0'` or `CURRENT_TIMESTAMP`
            defaultValue = type.cast(type.uncast(defaultValue));
        }
        catch (_d) {
            defaultValue = null;
        }
        Object.assign(this, __assign(__assign({ name: name, primaryKey: false, allowNull: !params.primaryKey }, params), { columnName: params.columnName || columnName, columnType: type.toSqlString().toLowerCase(), type: type, defaultValue: defaultValue, dataType: dataType, jsType: findJsType(DataTypes, type, dataType), virtual: type.virtual }));
    }
    Attribute.prototype.equals = function (columnInfo) {
        var e_1, _a;
        if (!columnInfo)
            return false;
        var props = ['allowNull', 'dataType', 'defaultValue', 'primaryKey'];
        try {
            for (var props_1 = __values(props), props_1_1 = props_1.next(); !props_1_1.done; props_1_1 = props_1.next()) {
                var prop = props_1_1.value;
                var source = this[prop];
                var target = columnInfo[prop];
                if (prop === 'dataType') {
                    if (source === 'integer' && target === 'int')
                        continue;
                    if (source !== target && Object.values(LENGTH_VARIANTS).includes(this.type.dataLength)) {
                        source = "".concat(this.type.dataLength).concat(source);
                    }
                }
                else if (prop === 'defaultValue') {
                    if (source === null && target === 'CURRENT_TIMESTAMP')
                        continue;
                }
                // SQLite has default value as string even if data type is integer
                if (source != target)
                    return false;
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (props_1_1 && !props_1_1.done && (_a = props_1.return)) _a.call(props_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return true;
    };
    Attribute.prototype.cast = function (value) {
        var castedValue = this.type.cast(value);
        return castedValue == null ? null : castedValue;
    };
    Attribute.prototype.uncast = function (value, strict) {
        var _this = this;
        if (strict === void 0) { strict = true; }
        if (Array.isArray(value) && this.jsType !== JSON) {
            return value.map(function (entry) { return _this.type.uncast(entry, strict); });
        }
        return this.type.uncast(value, strict);
    };
    return Attribute;
}());
module.exports = Attribute;
//# sourceMappingURL=attribute.js.map